import PropTypes from "prop-types";
import React, { useState } from "react";

import cities from "../../cities.json";
import statesData from "../../states.json";

function IdentityVerificationStep({ formData, updateFormData, nextStep }) {
    const [errors, setErrors] = useState({});

    const validateFields = () => {
        const newErrors = {};

        if(!formData.firstName?.trim())
            newErrors.firstName = "First name is required";
        if(!formData.lastName?.trim())
            newErrors.lastName = "Last name is required";
        // Date of birth moved to the Investment Limits step
        if(!formData.address1?.trim())
            newErrors.address1 = "Address is required";
        if(!formData.city?.trim()) newErrors.city = "City is required";
        if(!formData.state) newErrors.state = "State is required";
        if(!formData.zipCode?.trim())
            newErrors.zipCode = "ZIP code is required";

        // ZIP code format validation
        if(formData.zipCode && !/^\d{5}$/.test(formData.zipCode)) {
            newErrors.zipCode = "Please enter a valid 5-digit ZIP code";
        }

        // SSN moved to the Investment Limits step

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if(validateFields()) {
            nextStep();
        }
    };

    const handleInputChange = (field, value) => {
        updateFormData({ [field]: value });

        // Clear error when user starts typing
        if(errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    // SSN formatting moved to InvestmentLimitsStep

    return (
        <div className="w-full max-w-2xl px-4 pt-8 pb-4">
            <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                    Help us verify your identity
                </h1>
                <p className="text-gray-600">
                    Before you can invest, we&apos;re required by law to collect
                    some information about you.
                </p>
            </div>

            <div className="space-y-6">
                {/* First Name and Last Name */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <input
                            type="text"
                            placeholder="First name"
                            value={formData.firstName || ""}
                            onChange={(e) =>
                                handleInputChange("firstName", e.target.value)
                            }
                            className={`w-full p-2 border rounded-lg text-base font-medium ${
                                errors.firstName
                                    ? "border-red-500 focus:ring-red-500"
                                    : "border-gray-300 focus:ring-navy focus:border-navy"
                            }`}
                        />
                        {errors.firstName && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.firstName}
                            </p>
                        )}
                    </div>
                    <div>
                        <input
                            type="text"
                            placeholder="Last name"
                            value={formData.lastName || ""}
                            onChange={(e) =>
                                handleInputChange("lastName", e.target.value)
                            }
                            className={`w-full p-2 border rounded-lg text-base font-medium ${
                                errors.lastName
                                    ? "border-red-500 focus:ring-red-500"
                                    : "border-gray-300 focus:ring-navy focus:border-navy"
                            }`}
                        />
                        {errors.lastName && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.lastName}
                            </p>
                        )}
                    </div>
                </div>

                {/* Date of Birth moved to Investment Limits step */}

                {/* Address */}
                <div>
                    <input
                        type="text"
                        placeholder="Address"
                        value={formData.address1}
                        onChange={(e) =>
                            handleInputChange("address1", e.target.value)
                        }
                        className={`w-full p-2 border rounded-lg text-base ${
                            errors.address1
                                ? "border-red-500 focus:ring-red-500"
                                : "border-gray-300 focus:ring-navy focus:border-navy"
                        }`}
                    />
                    {errors.address1 && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.address1}
                        </p>
                    )}
                </div>

                {/* State and City */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <select
                            value={formData.state}
                            onChange={(e) =>
                                handleInputChange("state", e.target.value)
                            }
                            className={`w-full p-2 border rounded-lg text-base bg-white ${
                                errors.state
                                    ? "border-red-500 focus:ring-red-500"
                                    : "border-gray-300 focus:ring-navy focus:border-navy"
                            }`}
                        >
                            <option value="">Region/Province</option>
                            {Object.keys(statesData).map((stateCode) => (
                                <option key={stateCode} value={stateCode}>
                                    {statesData[stateCode]}
                                </option>
                            ))}
                        </select>
                        {errors.state && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.state}
                            </p>
                        )}
                    </div>

                    <div>
                        <select
                            value={formData.city}
                            onChange={(e) =>
                                handleInputChange("city", e.target.value)
                            }
                            className={`w-full p-2 border rounded-lg text-base bg-white ${
                                errors.city
                                    ? "border-red-500 focus:ring-red-500"
                                    : "border-gray-300 focus:ring-navy focus:border-navy"
                            }`}
                        >
                            <option value="">Select City</option>
                            {formData.state &&
                                cities[formData.state]?.map((city) => (
                                    <option key={city} value={city}>
                                        {city}
                                    </option>
                                ))}
                        </select>
                        {errors.city && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.city}
                            </p>
                        )}
                    </div>
                </div>

                {/* Country and Postal Code */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <select
                            value="US"
                            disabled
                            className="w-full p-2 border rounded-lg text-base bg-gray-100 text-gray-600"
                        >
                            <option value="US">United States</option>
                        </select>
                    </div>

                    <div>
                        <input
                            type="text"
                            placeholder="Postal Code"
                            value={formData.zipCode}
                            onChange={(e) =>
                                handleInputChange("zipCode", e.target.value)
                            }
                            maxLength={5}
                            className={`w-full p-2 border rounded-lg text-base ${
                                errors.zipCode
                                    ? "border-red-500 focus:ring-red-500"
                                    : "border-gray-300 focus:ring-navy focus:border-navy"
                            }`}
                        />
                        {errors.zipCode && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.zipCode}
                            </p>
                        )}
                    </div>
                </div>

                {/* SSN moved to Investment Limits step */}

                {/* Security notice */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 rounded-full bg-navy flex items-center justify-center">
                            <svg
                                className="w-3 h-3 text-white"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <span className="text-sm text-gray-600">
                            We encrypt and safeguard your information
                        </span>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-end mt-8">
                <button
                    onClick={handleNext}
                    className="px-8 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-2 transition-all shadow-lg"
                >
                    Next
                </button>
            </div>
        </div>
    );
}

IdentityVerificationStep.propTypes = {
    formData: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        address1: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zipCode: PropTypes.string,
    }).isRequired,
    updateFormData: PropTypes.func.isRequired,
    nextStep: PropTypes.func.isRequired,
    prevStep: PropTypes.func,
};

export default IdentityVerificationStep;